import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Autocomplete } from 'src/components/Autocomplete';
import { Checkbox } from 'src/components/Checkbox';
import { Chip } from 'src/components/Chip';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { useNativeBridge } from 'src/contexts/NativeBridgeContext';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { fetchTagsByType } from 'src/models/tags/tags';

interface TagsFilterValue {
  values?: string[];
  matchAll?: boolean;
}
interface TagsFilterConfig {
  field: string;
  label: string;
  value: any;
  type?:
  | 'client'
  | 'trainer'
  | 'product'
  | 'package'
  | 'exercise'
  | 'workoutPlan'
  | 'resourceTagHeaders'
  | 'video'
  | 'videoTagHeaders'
  | 'groupResource'
  | 'groupAssessment'
  | 'publicExercise';
  setValue: (newVal: TagsFilterValue | null) => void;
  valueOnly?: boolean;
  showMatchAll?: boolean;
  preserveObjectFormat?: boolean;
  showLabelPrefix?: boolean;
}
export const TagsFilter: React.FC<TagsFilterConfig> = ({
  label,
  value,
  setValue,
  valueOnly,
  type = 'client',
  showMatchAll = true,
  preserveObjectFormat = false,
  showLabelPrefix = false,
}) => {
  const { session } = useOptionalSession();
  const { isWebview } = useNativeBridge();
  const smDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const [listOpen, setListOpen] = useState(false);

  // App webview + narrow filter drawers: keep Autocomplete UI but block typing so the
  // OS keyboard does not cover the tag list. Desktop keeps type-to-filter.
  const tapOnlyTagPicker = smDown || !!isWebview;

  const { data: workoutPlanTags, isLoading: workoutPlanTagsLoading } = useQuery(
    {
      queryKey: ['workoutPlanTags'],
      queryFn: () =>
        fetchTagsByType(
          'workout-plan,difficulty,equipment,badge,workout-time,frequency,objective,alternate-video'
        ),
      enabled: type === 'workoutPlan',
      gcTime: 60000,
    }
  );

  // Normalize value to handle both string (comma-separated) and object formats
  const normalizedValue = typeof value === 'string'
    ? { values: value.split(',').map((t) => t.trim()).filter(Boolean), matchAll: false }
    : value;

  const setTags = (newValues) => {
    if (!newValues.length) {
      setValue(null);
      return;
    }
    // When showMatchAll is false, output a simple comma-separated string
    // Otherwise, output the object format for backward compatibility
    if (!showMatchAll && !preserveObjectFormat) {
      setValue(newValues.join(',') as any);
      return;
    }
    setValue({ values: newValues, matchAll: normalizedValue?.matchAll });
  };

  const options =
    type === 'workoutPlan'
      ? [...new Set(workoutPlanTags?.map((t) => t.name) || [])]
      : type.includes('TagHeaders')
      ? session?.[type] || []
      : session?.[`${type}Tags`] || [];

  if (valueOnly) {
    const selectedTags = normalizedValue?.values?.join(', ') || '';
    if (showLabelPrefix && selectedTags) {
      return <>{`${label}: ${selectedTags}`}</>;
    }
    return selectedTags;
  }

  return (
    <Stack>
      <Autocomplete
        loading={workoutPlanTagsLoading}
        multiple
        options={options}
        getOptionLabel={(tag) => tag}
        value={normalizedValue?.values || []}
        onChange={(_, newValue) => setTags(newValue)}
        openOnFocus
        {...(tapOnlyTagPicker
          ? {
              open: listOpen,
              onOpen: () => setListOpen(true),
              onClose: () => setListOpen(false),
            }
          : {})}
        slotProps={{
          listbox: {
            sx: {
              maxHeight: tapOnlyTagPicker ? 'min(45vh, 320px)' : 280,
              overscrollBehavior: 'contain',
              WebkitOverflowScrolling: 'touch',
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={!normalizedValue?.values?.length ? 'Any' : ''}
            sx={{ pl: 0 }}
            disabled={tapOnlyTagPicker ? 'click-only' : params.disabled}
            onClick={tapOnlyTagPicker ? () => setListOpen(true) : undefined}
          />
        )}
        renderTags={(value: readonly string[], getTagProps) =>
          value.map((option: string, index: number) => {
            const { key, ...tagProps } = getTagProps({ index });
            return <Chip label={option} key={key} {...tagProps} />;
          })
        }
      />
      {showMatchAll && !!normalizedValue?.values?.length && (
        <Checkbox
          checked={!!normalizedValue?.matchAll}
          onChange={(e) => setValue({ ...normalizedValue, matchAll: e.target.checked })}
          label="Matches with ALL these tags"
          sx={{ mt: 2 }}
        />
      )}
    </Stack>
  );
};
